import { useState, useEffect, useRef } from 'react';
import { doc, setDoc, getDoc, getFirestore } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useAuth } from '../hooks/useAuth';
import { useLoader } from '../contexts/LoaderContext';
import imageCompression from 'browser-image-compression';
import { logError, logEvent } from '../utils/analytics';
import moment from 'moment';

export const useShopConfig = () => {
  const { user } = useAuth();
  const { showLoader, hideLoader } = useLoader();

  // Refs for mutable values
  const stateRef = useRef({
    user,
    setupCompleted: false,
    isLoading: false
  });

  // Refs for DOM elements and components
  const toastRef = useRef(null);
  const fileUploadRef = useRef(null);
  const markerRef = useRef(null);

  const [userRecord, setUserRecord] = useState(null);

  useEffect(() => {
    if (!user) return;

    const fetchUserRecord = async () => {
      try {
        const db = getFirestore();
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        setUserRecord(userDoc.data());
      } catch (error) {
        logError('useShopConfig: fetch user record', error);
      }
    };

    fetchUserRecord();
  }, [user]);

  const deleteImage = async (imageUrl) => {
    try {
      const storage = getStorage();
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);
      return true;
    } catch (error) {
      logError('useShopConfig: delete image', error);
      return false;
    }
  };

  const saveShopConfig = async (values) => {
    if (!user?.uid) return;

    stateRef.current.isLoading = true;

    try {
      const currentStoreImages = userRecord?.storeImages || [];
      const incomingStoreImages = values.storeImages || [];

      let finalImageUrls = [];

      if (!incomingStoreImages.length) {
        if (currentStoreImages.length > 0) {
          await Promise.all(
            currentStoreImages.map(img => deleteImage(img))
          );
        }
        finalImageUrls = [];
      } else {
        const storage = getStorage();
        const uploadPromises = incomingStoreImages.map(async (image) => {
          let file;
          
          // If image is a Firebase URL, keep it as is
          if (typeof image === 'string' && image.includes('firebasestorage.googleapis.com')) {
            return image;
          }

          // If image is a preview object with file property
          if (image && image.file) {
            file = image.file;
          }
          
          // If image is a File or Blob object directly
          else if (image instanceof File || image instanceof Blob) {
            file = image;
          }
          
          // If image is an object with objectURL, convert it to File
          else if (image && image.objectURL) {
            const response = await fetch(image.objectURL);
            const blob = await response.blob();
            file = new File([blob], 'store-image.webp', { type: blob.type });
          }

          // If we have a valid file, compress and upload
          if (file) {
            const compressedFile = await imageCompression(file, {
              maxSizeMB: 1,
              maxWidthOrHeight: 600,
              useWebWorker: true,
              fileType: 'image/webp'
            });

            const storageRef = ref(storage, `store-images/${user.uid}/${Date.now()}-${Math.random().toString(36).substring(2)}.webp`);
            await uploadBytes(storageRef, compressedFile);
            return getDownloadURL(storageRef);
          }

          throw new Error('Invalid image format. Please provide a valid image file.');
        });

        finalImageUrls = await Promise.all(uploadPromises);
      }

      const db = getFirestore();
      const userRef = doc(db, "users", user.uid);

      // Create shopData with only allowed fields (do NOT set setupCompleted here)
      const shopData = {
        contactEmail: values.contactEmail,
        contactPhone: values.contactPhone,
        createdAt: moment(userRecord?.createdAt).unix() || moment().unix(),
        directions: values.directions,
        email: values.email,
        isActive: true,
        lastActivity: moment().unix(),
        lastLogin: moment().unix(),
        markerPosition: values.markerPosition,
        shopName: values.shopName,
        shopType: Array.isArray(values.shopType) ? values.shopType : [values.shopType],
        storeAddress: values.storeAddress,
        storeImages: finalImageUrls,
        uid: user.uid,
        updatedAt: moment().unix(),
        isMainUser: values.isMainUser || false,
        assignedFeature: values.isMainUser ? 'all' : userRecord?.assignedFeature || []
      };

      // Remove any undefined values
      Object.keys(shopData).forEach(key => {
        if (shopData[key] === undefined) {
          delete shopData[key];
        }
      });

      // Update user document
      await setDoc(userRef, shopData, { merge: true });

      // Update businessAtAGlance with the first store image if available
      if (finalImageUrls.length > 0) {
        try {
          const businessAtAGlanceRef = doc(db, 'businessAtAGlance', user.uid);
          await setDoc(businessAtAGlanceRef, {
            storeImage: finalImageUrls[0],
            updatedAt: moment().unix()
          }, { merge: true });
        } catch (error) {
          console.error('Error updating businessAtAGlance:', error);
          // Don't fail the whole operation if this update fails
        }
      }

      const updatedRecord = { ...userRecord, ...shopData };
      setUserRecord(updatedRecord);
      stateRef.current.user = updatedRecord;

      logEvent('shop_config_saved', {
        is_update: !!userRecord?.setupCompleted,
        has_images: finalImageUrls.length > 0
      });


      toastRef.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: userRecord?.setupCompleted ? 'Shop configuration updated' : 'Shop configuration saved',
        life: 3000
      });

      return true;
    } catch (error) {
      logError('saveShopConfig', error);
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to save shop configuration',
        life: 3000
      });
      return false;
    } finally {
      stateRef.current.isLoading = false;
    }
  };

  const handleSaveClick = async (values, isFinalSubmit, callback) => {
    try {
      showLoader({
        title: 'Saving Shop Configuration',
        subText: 'Please wait...'
      });

      const success = await saveShopConfig(values);

      if (success) {
        try {
          const db = getFirestore();
          const businessAtAGlanceRef = doc(db, 'businessAtAGlance', user.uid);
          
          // First, check if the document exists
          const docSnap = await getDoc(businessAtAGlanceRef);
          
          // Prepare the data to be saved
          const updateData = {
            ...(values.markerPosition && { markerPosition: values.markerPosition }),
            ...(values.storeAddress && { storeAddress: values.storeAddress }),
            ...(values.directions && { directions: values.directions }),
            ...(values.shopName && { shopName: values.shopName }),
            ...(values.contactEmail && { contactEmail: values.contactEmail }),
            updatedAt: moment().unix(),
          };

          if (docSnap.exists()) {
            // Document exists, update it with merge
            await setDoc(businessAtAGlanceRef, updateData, { merge: true });
          } else {
            // Document doesn't exist, create it with required fields
            await setDoc(businessAtAGlanceRef, {
              ...updateData,
              uid: user.uid,
              createdAt: moment().unix(),
              setupCompleted: false
            });
          }
          
          logEvent('business_at_a_glance_updated', {
            user_id: user.uid,
            has_marker: !!values.markerPosition,
            has_address: !!values.storeAddress,
            has_directions: !!values.directions,
            is_new: !docSnap.exists()
          });
        } catch (error) {
          logError('update_business_at_a_glance', error);
          console.warn('Could not update businessAtAGlance:', error);
        }
      }

      if (success && isFinalSubmit) {
        // Don't show toast or navigate yet - let the parent component handle that
        return true;
      }

      return success;
    } catch (error) {
      logError('useShopConfig: save click', error);
      if (isFinalSubmit) {
        // Show error toast but don't navigate
        toastRef.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to complete setup. Please try again.',
          life: 3000
        });
      }
      return false;
    } finally {
      hideLoader();
    }
  };

  return {
    userRecord,
    handleSaveClick
  };
};